import { UserRole } from '@common/interfaces/user'
import { ROUTES } from '../../constants/routes'
import { IMenuData } from '../SideMenu/SideMenuConfig'

/**
 * Which destinations get a bottom-tab slot, per role, in tab order.
 *
 * Keys rather than copies of the items: label, icon, link and badge stay owned by
 * SideMenuConfig, so a rename, a new icon or a new badge reaches the tab bar for free and
 * the rail and the bar cannot drift apart.
 *
 * A key that is not in the role's own menu is skipped rather than rendered, so the bar can
 * never offer a destination the user has no route for — which also means these lists are
 * safe to be slightly optimistic.
 */
export const MOBILE_TAB_KEYS: Partial<Record<UserRole, readonly string[]>> = {
  [UserRole.ADMIN]: [
    ROUTES.MY_TASKS,
    ROUTES.MESSAGES,
    ROUTES.CLIENT_MANAGEMENT,
    ROUTES.ACTIVITY_QUEUE,
  ],
  [UserRole.PORTFOLIO_ADMIN]: [
    ROUTES.MY_TASKS,
    ROUTES.MESSAGES,
    ROUTES.CLIENT_MANAGEMENT,
    ROUTES.ACTIVITY_QUEUE,
  ],
  [UserRole.PORTFOLIO_USER]: [
    ROUTES.MY_TASKS,
    ROUTES.MESSAGES,
    ROUTES.CLIENT_MANAGEMENT,
    ROUTES.ACTIVITY_QUEUE,
  ],
  [UserRole.UW_USER]: [
    ROUTES.MY_TASKS,
    ROUTES.MESSAGES,
    ROUTES.DUE_DILIGENCE_HOME,
    ROUTES.CLIENT_MANAGEMENT,
  ],
  [UserRole.BDO]: [ROUTES.MY_TASKS, ROUTES.MESSAGES, ROUTES.PROSPECTS, ROUTES.CRM_HOME],
  [UserRole.PARTICIPANT_USER]: [ROUTES.CLIENT_MANAGEMENT, ROUTES.OUTGOING_WIRE],
  // Their whole rail, so the bar is the rail on mobile.
  [UserRole.TASKS_AND_MESSAGING]: [ROUTES.MY_TASKS, ROUTES.MESSAGES],
}

/**
 * The most tabs the bar will show.
 *
 * Four plus More. A five-across bar on a 375px screen gives each tab under 70px, which is
 * not enough for an icon and a legible label; the fifth slot is worth more as the door to
 * everything else than as one more destination.
 */
export const MAX_TABS = 4

export interface IMobileNav {
  /** Rendered in the bar, in `MOBILE_TAB_KEYS` order. */
  tabs: IMenuData[]
  /** Everything else, in the sidebar's own order, shown in the More sheet. */
  more: IMenuData[]
}

/**
 * Splits a role's menu into the bar and the More sheet.
 *
 * A short menu (the client portal's five items, the past-client two) goes entirely into the
 * bar with no More tab: a sheet holding nothing is a control that lies about having
 * content behind it.
 */
export const resolveMobileNav = (
  items: IMenuData[] = [],
  keys: readonly string[] = [],
): IMobileNav => {
  if (items.length === 0) {
    return { tabs: [], more: [] }
  }

  // Short enough to show whole. `MAX_TABS + 1` because with nothing in the sheet the
  // More slot is free for a real destination.
  if (items.length <= MAX_TABS + 1) {
    return { tabs: items, more: [] }
  }

  const byKey = new Map(items.map((item) => [item.key, item]))
  const tabs = keys
    .map((key) => byKey.get(key))
    .filter((item): item is IMenuData => !!item)
    .slice(0, MAX_TABS)

  // No configured keys for this role (or none of them matched) — fall back to the first
  // few menu items rather than rendering a bar with nothing in it.
  const resolvedTabs = tabs.length > 0 ? tabs : items.slice(0, MAX_TABS)
  const taken = new Set(resolvedTabs.map((item) => item.key))

  return { tabs: resolvedTabs, more: items.filter((item) => !taken.has(item.key)) }
}

export default resolveMobileNav
